import {useCallback, useEffect, useMemo, useRef, memo} from "react";
import {REGEXP_ONLY_DIGITS} from "input-otp";

import {InputOTP, InputOTPGroup, InputOTPSlot} from "@/components/ui/input-otp";
import {onAndroid} from "@/utils/detectors";

interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  length?: number;
}

// Sanitize the input by removing non-digit characters and limiting its length.
const sanitize = (rawValue: string, maxLength: number) =>
  rawValue.replace(/\D/g, "").slice(0, maxLength);

const OTPInput = ({value, onChange, onComplete, length = 6}: OTPInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const normalizedValue = useMemo(
    () => sanitize(value ?? "", length),
    [value, length],
  );
  const previousValueRef = useRef(normalizedValue);

  const focusAt = useCallback(
    (position: number) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      requestAnimationFrame(() => {
        input.focus();
        const clamped = Math.max(0, Math.min(position, length));
        input.setSelectionRange(clamped, clamped);
      });
    },
    [length],
  );

  const focusFirstSlot = useCallback(() => {
    focusAt(0);
  }, [focusAt]);

  useEffect(() => {
    focusFirstSlot();
  }, [focusFirstSlot]);

  useEffect(() => {
    if (normalizedValue === "" && previousValueRef.current.length > 0) {
      focusFirstSlot();
    }
    previousValueRef.current = normalizedValue;
  }, [normalizedValue, focusFirstSlot]);

  const handleComplete = useCallback(
    (code: string) => {
      if (!onComplete) {
        return;
      }

      const sanitizedValue = sanitize(code, length);
      if (sanitizedValue.length === length) {
        onComplete(sanitizedValue);
      }
    },
    [length, onComplete],
  );

  const handleChange = useCallback(
    (nextValue: string) => {
      const sanitizedValue = sanitize(nextValue, length);

      onChange(sanitizedValue);

      // Auto-submit on Android when the OTP is fully entered.
      // onComplete is not called on Android keyboards when pasting with spaces.
      // TODO: Verify this behavior after update of input-otp library.
      if (onAndroid && sanitizedValue.length === length) {
        void handleComplete(sanitizedValue);
      }
    },
    [length, onChange, handleComplete],
  );

  return (
    <div className="flex w-full flex-col items-center">
      <InputOTP
        ref={inputRef}
        value={normalizedValue}
        // Allow Android keyboards to include separators without truncating the code.
        maxLength={onAndroid ? length + 1 : length}
        onChange={handleChange}
        onComplete={onAndroid ? undefined : handleComplete}
        // Enable to paste full OTP with space at once on Android devices.
        pattern={onAndroid ? "^[0-9 ]*$" : REGEXP_ONLY_DIGITS}
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label="One-time password"
        pasteTransformer={(pastedValue) => sanitize(pastedValue, length)}
      >
        <InputOTPGroup>
          {Array.from({length}).map((_, index) => (
            <InputOTPSlot
              key={index}
              index={index}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
    </div>
  );
};

export default memo(OTPInput);
